import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withMethods,
  withProps,
} from '@ngrx/signals';
import {
  addEntity,
  entityConfig,
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  withEntitiesLoadingCall,
  withCallStatus,
  withCalls,
  withLogger,
  callConfig,
} from '@ngrx-traits/signals';
import { map, catchError, of, concat, last, toArray } from 'rxjs';
import {
  CaseNavigationItem,
  CaseNavigationItemDto,
  NavigationConfigListItem,
  CASE_SIDEBAR_PAGES,
  DEFAULT_PAGE_VISIBILITY,
} from '../models/navigation-config.model';
import { DefaultNavigationConfigService } from '../data/navigation-config.service';

const defaultNavEntityConfig = entityConfig({
  entity: type<CaseNavigationItem>(),
  collection: 'defaultNav',
  selectId: (item: CaseNavigationItem) => item.id,
});

export const DefaultNavigationConfigStore = signalStore(
  withEntities(defaultNavEntityConfig),
  withCallStatus({ collection: 'defaultNav', initialValue: 'loading' }),
  withLogger({
    name: 'DefaultNavigationConfigStore',
  }),
  withProps(() => ({
    _service: inject(DefaultNavigationConfigService),
  })),
  withEntitiesLoadingCall({
    collection: 'defaultNav',
    fetchEntities: ({ _service }) =>
      _service.get().pipe(
        map((response) => ({
          entities: response.result,
          total: response.count,
        })),
        catchError(() => of({ entities: [], total: 0 }))
      ),
  }),
  withComputed((store) => ({
    availableItems: computed<NavigationConfigListItem[]>(() => {
      const configuredKeys = new Set(
        store.defaultNavEntities().map((e) => e.name)
      );
      return CASE_SIDEBAR_PAGES.filter(
        (page) => !configuredKeys.has(page.key)
      ).map((page) => ({
        key: page.key,
        label: page.label,
        description: page.description,
        visible: DEFAULT_PAGE_VISIBILITY[page.key],
        orderPosition: 0,
      }));
    }),
    configuredItems: computed<NavigationConfigListItem[]>(() => {
      return [...store.defaultNavEntities()]
        .sort((a, b) => a.orderPosition - b.orderPosition)
        .map((entity) => {
          const page = CASE_SIDEBAR_PAGES.find((p) => p.key === entity.name);
          return {
            key: entity.name,
            label: page?.label ?? entity.name,
            description: page?.description,
            visible: entity.isVisible,
            orderPosition: entity.orderPosition,
            id: entity.id,
          };
        });
    }),
  })),
  withCalls(({ _service, ...store }) => ({
    createItem: callConfig({
      call: (dto: CaseNavigationItemDto) => _service.create(dto),
      onSuccess: (newItem) => {
        patchState(store, addEntity(newItem, defaultNavEntityConfig));
      },
    }),

    updateItem: callConfig({
      call: ({
        id,
        data,
      }: {
        id: number;
        data: Partial<CaseNavigationItemDto>;
      }) => _service.update(id, data).pipe(map(() => ({ id, data }))),
      onSuccess: ({ id, data }) => {
        patchState(
          store,
          updateEntity(
            {
              id,
              changes: {
                ...(data.orderPosition !== undefined && {
                  orderPosition: data.orderPosition,
                }),
                ...(data.isVisible !== undefined && {
                  isVisible: data.isVisible,
                }),
              },
            },
            defaultNavEntityConfig
          )
        );
      },
    }),

    deleteItem: callConfig({
      call: (id: number) => _service.delete(id).pipe(map(() => id)),
      onSuccess: (id) => {
        patchState(store, removeEntity(id, defaultNavEntityConfig));
      },
    }),

    reorderItems: callConfig({
      call: (updates: { id: number; orderPosition: number }[]) => {
        if (updates.length === 0) return of(updates);
        return concat(
          ...updates.map(({ id, orderPosition }) =>
            _service
              .update(id, { orderPosition })
              .pipe(map(() => ({ id, orderPosition })))
          )
        ).pipe(toArray());
      },
      onSuccess: (results) => {
        results.forEach(({ id, orderPosition }) => {
          patchState(
            store,
            updateEntity(
              { id, changes: { orderPosition } },
              defaultNavEntityConfig
            )
          );
        });
      },
    }),

    createAllItems: callConfig({
      call: (items: CaseNavigationItemDto[]) => {
        if (items.length === 0) return of([]);
        return concat(...items.map((dto) => _service.create(dto))).pipe(
          toArray()
        );
      },
      onSuccess: (newItems) => {
        newItems.forEach((item) => {
          patchState(store, addEntity(item, defaultNavEntityConfig));
        });
      },
    }),

    deleteAllItems: callConfig({
      call: (ids: number[]) => {
        if (ids.length === 0) return of([]);
        return concat(
          ...ids.map((id) => _service.delete(id).pipe(map(() => id)))
        ).pipe(toArray());
      },
      onSuccess: (deletedIds) => {
        deletedIds.forEach((id) => {
          patchState(store, removeEntity(id, defaultNavEntityConfig));
        });
      },
    }),

    migrateFromLocal: callConfig({
      call: () => {
        const dtos: CaseNavigationItemDto[] = CASE_SIDEBAR_PAGES.map(
          (page, index) => ({
            name: page.key,
            orderPosition: index,
            isVisible: DEFAULT_PAGE_VISIBILITY[page.key],
          })
        );
        return concat(...dtos.map((dto) => _service.create(dto))).pipe(last());
      },
      onSuccess: () => {
        store.setDefaultNavEntitiesLoading();
      },
    }),
  }))
);
